import {
  Component,
  ElementRef,
  OnDestroy,
  afterRenderEffect,
  input,
  output,
  viewChild
} from '@angular/core';

import { fitIntoViewport } from '@shared/utils/fit-into-viewport';

import { ContextMenuOption } from './context-menu.component';
import { SubmenuState } from './submenu-state';

@Component({
  selector: 'app-context-menu-submenu',
  imports: [],
  templateUrl: './context-menu-submenu.component.html',
  standalone: true
})
export class ContextMenuSubmenuComponent implements OnDestroy {
  options = input.required<ContextMenuOption[]>();
  showIcons = input<boolean>(false);
  optionClicked = output<ContextMenuOption>();

  private submenuState = new SubmenuState();

  readonly openSubMenuIndex = this.submenuState.openIndex;

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');

  constructor() {
    // Opened beside its parent item, the submenu grows downward and leaves the viewport when
    // that item sits near the bottom.
    // Only vertically: shifted sideways it would cover the parent menu it opens from.
    afterRenderEffect(() => {
      this.options();
      fitIntoViewport(this.panel().nativeElement, 'vertical');
    });
  }

  ngOnDestroy(): void {
    this.submenuState.reset();
  }

  hasSubOptions(option: ContextMenuOption): boolean {
    return !!option.subOptions && option.subOptions.length > 0;
  }

  showSubMenu(index: number, option: ContextMenuOption): void {
    this.submenuState.hoverEnter(index, this.hasSubOptions(option));
  }

  hideSubMenu(): void {
    this.submenuState.hoverLeave();
  }

  onOptionClick(option: ContextMenuOption): void {
    if (option.disabled) {
      return;
    }

    if (this.hasSubOptions(option)) {
      this.submenuState.togglePin(this.options().indexOf(option));
      return;
    }

    this.optionClicked.emit(option);
  }

  protected trackMenuOptionByLabelAndIndex(
    index: number,
    option: ContextMenuOption
  ): string {
    return `${option.label}_${index}`;
  }

  protected itemClasses(option: ContextMenuOption): string {
    if (option.disabled) {
      return 'cursor-not-allowed text-text-tertiary';
    }
    if (option.isDanger) {
      return 'cursor-pointer text-danger hover:bg-danger-soft hover:text-danger-hover';
    }
    return 'cursor-pointer text-text-primary hover:bg-interactive';
  }

  protected iconClasses(option: ContextMenuOption): string {
    if (option.disabled) return 'text-text-tertiary';
    if (option.isDanger) return 'text-danger';
    return 'text-text-secondary';
  }
}
